import React, { useState, useEffect } from 'react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import Icon from '../../../components/AppIcon';
import overtimeSettingsService from '../../../services/overtimeSettingsService';
import NotificationToast from '../../../components/ui/NotificationToast';

const OvertimeSettings = ({ employeeId = null, employeeName = null, allowGlobal = true }) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [notification, setNotification] = useState(null);
  const [settings, setSettings] = useState({
    overtime_considered_after_minutes: 55,
    extra_hours_pay_type: 'fixed_hourly_rate',
    extra_hours_pay_amount: 110.00,
    public_holiday_pay_type: 'fixed_daily_rate',
    public_holiday_pay_amount: 1100.00,
    week_off_pay_type: 'fixed_daily_rate',
    week_off_pay_amount: 1100.00
  });

  const isGlobalSettings = !employeeId;
  const isSaveDisabled = saving || (isGlobalSettings && !allowGlobal);

  useEffect(() => {
    loadSettings();
  }, [employeeId]);

  const loadSettings = async () => {
    setLoading(true);
    try {
      const result = await overtimeSettingsService.getOvertimeSettings(employeeId);
      if (result?.success && result.data) {
        // Handle both single object and array response
        const settingsData = Array.isArray(result.data) ? result.data[0] : result.data;
        if (settingsData) {
          setSettings({
            overtime_considered_after_minutes: settingsData.overtime_considered_after_minutes || 55,
            extra_hours_pay_type: settingsData.extra_hours_pay_type || 'fixed_hourly_rate',
            extra_hours_pay_amount: parseFloat(settingsData.extra_hours_pay_amount) || 110.00,
            public_holiday_pay_type: settingsData.public_holiday_pay_type || 'fixed_daily_rate',
            public_holiday_pay_amount: parseFloat(settingsData.public_holiday_pay_amount) || 1100.00,
            week_off_pay_type: settingsData.week_off_pay_type || 'fixed_daily_rate',
            week_off_pay_amount: parseFloat(settingsData.week_off_pay_amount) || 1100.00
          });
        }
      }
    } catch (error) {
      console.error('Error loading overtime settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const result = await overtimeSettingsService.saveOvertimeSettings(settings, employeeId);
      if (result?.success) {
        setNotification({
          message: employeeId 
            ? `Overtime settings saved for ${employeeName || 'employee'}` 
            : 'Global overtime settings saved successfully',
          type: 'success'
        });
      } else {
        setNotification({
          message: result?.error || 'Failed to save overtime settings',
          type: 'error'
        });
      }
    } catch (error) {
      setNotification({
        message: 'An error occurred while saving settings',
        type: 'error'
      });
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (field, value) => {
    setSettings(prev => ({
      ...prev,
      [field]: value
    }));
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          <span className="ml-3 text-muted-foreground">Loading overtime settings...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <NotificationToast
        message={notification?.message}
        type={notification?.type}
        onClose={() => setNotification(null)}
      />

      <div className="mb-6">
        <h3 className="text-lg font-semibold text-foreground mb-2">
          Overtime Consideration Settings
        </h3>
        {employeeId && (
          <p className="text-sm text-muted-foreground">
            Settings for: <strong>{employeeName || 'Employee'}</strong>
          </p>
        )}
        {!employeeId && (
          <p className="text-sm text-muted-foreground">
            Global default settings (applies to all employees unless overridden)
          </p>
        )}
        {isGlobalSettings && !allowGlobal && (
          <p className="text-sm text-amber-600 mt-2">
            Save the employee first to configure overtime settings.
          </p>
        )}
      </div>

      {/* Working Days Section */}
      <div className="mb-6 p-4 bg-card border border-border rounded-lg">
        <h4 className="text-md font-semibold text-foreground mb-4">Working Days</h4>
        
        <div className="space-y-4">
          {/* Overtime Considered After */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Overtime considered after <span className="text-red-500">*</span>
            </label>
            <div className="flex items-center gap-2">
              <Input
                type="number"
                min="0"
                value={settings.overtime_considered_after_minutes}
                onChange={(e) => handleChange('overtime_considered_after_minutes', parseInt(e.target.value) || 0)}
                className="flex-1"
              />
              <span className="text-sm text-muted-foreground whitespace-nowrap">mins</span>
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              Minutes after regular work hours when overtime starts
            </p>
          </div>

          {/* Extra Hours Pay */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Extra Hours Pay <span className="text-red-500">*</span>
            </label>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Select
                value={settings.extra_hours_pay_type}
                onChange={(value) => handleChange('extra_hours_pay_type', value)}
                options={[
                  { value: 'fixed_hourly_rate', label: 'Fixed Hourly Rate' },
                  { value: 'double_time', label: 'Double Time' },
                  { value: 'time_and_half', label: 'Time and Half' }
                ]}
                placeholder="Select pay type"
              />
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground">Amount <span className="text-red-500">*</span></span>
                <div className="flex items-center gap-1 flex-1">
                  <span className="text-sm text-muted-foreground">₹</span>
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    value={settings.extra_hours_pay_amount}
                    onChange={(e) => handleChange('extra_hours_pay_amount', parseFloat(e.target.value) || 0)}
                    className="flex-1"
                  />
                  <span className="text-sm text-muted-foreground whitespace-nowrap">/hour</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Weekoffs and Holidays Section */}
      <div className="mb-6 p-4 bg-card border border-border rounded-lg">
        <h4 className="text-md font-semibold text-foreground mb-4">Weekoffs and Holidays</h4>
        
        <div className="space-y-4">
          {/* Public Holiday Pay */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Public Holiday Pay <span className="text-red-500">*</span>
            </label>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Select
                value={settings.public_holiday_pay_type}
                onChange={(value) => handleChange('public_holiday_pay_type', value)}
                options={[
                  { value: 'fixed_daily_rate', label: 'Fixed Daily Rate' },
                  { value: 'double_time', label: 'Double Time' },
                  { value: 'time_and_half', label: 'Time and Half' }
                ]}
                placeholder="Select pay type"
              />
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground">Amount <span className="text-red-500">*</span></span>
                <div className="flex items-center gap-1 flex-1">
                  <span className="text-sm text-muted-foreground">₹</span>
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    value={settings.public_holiday_pay_amount}
                    onChange={(e) => handleChange('public_holiday_pay_amount', parseFloat(e.target.value) || 0)}
                    className="flex-1"
                  />
                  <span className="text-sm text-muted-foreground whitespace-nowrap">/day</span>
                </div>
              </div>
            </div>
          </div>

          {/* Week Off Pay */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Week Off Pay <span className="text-red-500">*</span>
            </label>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Select
                value={settings.week_off_pay_type}
                onChange={(value) => handleChange('week_off_pay_type', value)}
                options={[
                  { value: 'fixed_daily_rate', label: 'Fixed Daily Rate' },
                  { value: 'double_time', label: 'Double Time' },
                  { value: 'time_and_half', label: 'Time and Half' }
                ]}
                placeholder="Select pay type"
              />
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground">Amount <span className="text-red-500">*</span></span>
                <div className="flex items-center gap-1 flex-1">
                  <span className="text-sm text-muted-foreground">₹</span>
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    value={settings.week_off_pay_amount}
                    onChange={(e) => handleChange('week_off_pay_amount', parseFloat(e.target.value) || 0)}
                    className="flex-1"
                  />
                  <span className="text-sm text-muted-foreground whitespace-nowrap">/day</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Save Button */}
      <div className="flex justify-end gap-3">
        <Button
          variant="outline"
          onClick={loadSettings}
          disabled={saving}
        >
          <Icon name="RefreshCw" size={16} className="mr-2" />
          Reset
        </Button>
        <Button
          onClick={handleSave}
          disabled={isSaveDisabled}
        >
          {saving ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              Saving...
            </>
          ) : (
            <>
              <Icon name="Save" size={16} className="mr-2" />
              Save Settings
            </>
          )}
        </Button>
      </div>
    </div>
  );
};

export default OvertimeSettings;

